import { MoreHorizontal } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { IconButton } from '@/design-system';
import { cn } from '@/shared/cn';
import { MENU_CONTENT, MENU_ITEM_BASE } from '@/shared/ui-class';

export type AdminRowMenuItem = {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
};

export function AdminRowMenu({
  label,
  items,
}: {
  label: string;
  items: AdminRowMenuItem[];
}) {
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-flex">
      <IconButton
        label={label}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <MoreHorizontal className="h-4 w-4" />
      </IconButton>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className={cn(MENU_CONTENT, 'absolute right-0 top-full z-50 mt-1')}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              className={cn(
                MENU_ITEM_BASE,
                'w-full text-left',
                item.disabled && 'cursor-not-allowed opacity-50',
                item.danger &&
                  'text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-[color-mix(in_srgb,var(--color-red-900)_30%,transparent)]',
              )}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
